// Helpers PUROS do canvas por estágios (sem React/React Flow). Testáveis isoladamente
// (sandbox-flow.test.ts). O modelo canônico é o pipeline em camadas (estágios): as edges
// PRODUCTION_FLOW são DERIVADAS da adjacência de estágios ativos consecutivos.
// `layeringOf`, `stagesFromLayering` e `deriveEdgesFromStages` MIGRARAM para
// `@opus-dt/shared-types` (`lib/scenario-structure-derive.ts`), porque o backend passou a semear o
// `structureSnap` na criação do cenário e precisa das MESMAS regras. Aqui só re-exportamos: manter
// uma segunda implementação aqui faria as duas pontas divergirem em silêncio.
import type { ScenarioEdge } from "@opus-dt/shared-types";
import {
  deriveEdgesFromStages as deriveEdgesShared,
  layeringOf as layeringOfShared,
  stagesFromLayering as stagesFromLayeringShared,
} from "@opus-dt/shared-types";
import type { SandboxMachine, SandboxStage } from "@/types/sim-sandbox.types";

export const layeringOf = layeringOfShared;
export const stagesFromLayering: (layer: Map<string, number>) => {
  stages: SandboxStage[];
  stageKeyOf: Map<string, string>;
} = stagesFromLayeringShared;

/** Estágios ordenados que contêm ≥1 máquina presente (os únicos que entram no fluxo). */
export function activeStages(stages: SandboxStage[], machines: SandboxMachine[]): SandboxStage[] {
  const present = new Set(machines.filter((m) => m.present).map((m) => m.stageKey));
  return [...stages].filter((s) => present.has(s.key)).sort((a, b) => a.order - b.order);
}

/** Máquinas presentes de um estágio. */
export function machinesInStage(stageKey: string, machines: SandboxMachine[]): SandboxMachine[] {
  return machines.filter((m) => m.present && m.stageKey === stageKey);
}

/** Deriva as edges máquina→máquina (todos-com-todos) entre estágios ATIVOS consecutivos.
 * START/END são só âncoras de canvas — não entram nas edges do snapshot (convenção).
 * Implementação em `@opus-dt/shared-types`; aqui só a assinatura tipada do sandbox. */
export function deriveEdgesFromStages(
  machines: SandboxMachine[],
  stages: SandboxStage[]
): ScenarioEdge[] {
  return deriveEdgesShared(machines, stages);
}

/** Assinatura estável da ESTRUTURA (p/ re-seed / detecção de mudança). Exclui x/y e simProfile
 * (posição de canvas e ajuste fino de parâmetro não mudam o grafo). */
export function structureSignature(input: {
  machines: SandboxMachine[];
  stages: SandboxStage[];
  productId: string | null;
  baseLineId: string | null;
}): string {
  const machines = [...input.machines]
    .map((m) => `${m.key}:${m.sourceMachineId}:${m.origin}:${m.present ? 1 : 0}:${m.stageKey}`)
    .sort()
    .join("|");
  const stages = [...input.stages]
    .map((s) => `${s.key}@${s.order}`)
    .sort()
    .join("|");
  return `${input.baseLineId ?? "new"}#${input.productId ?? "-"}#${stages}#${machines}`;
}
